/**
 * Utility function to sanitize error objects and prevent React serialization issues.
 * React error #31 occurs when non-serializable objects (like database errors with circular references)
 * are passed to React components.
 */

/**
 * Checks if a value is serializable for React
 * @param {any} value - The value to check
 * @returns {boolean} - True if serializable, false otherwise
 */
export const isSerializable = (value) => {
  if (value === null || value === undefined) return true;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return true;
  if (Array.isArray(value)) return value.every(isSerializable);
  if (typeof value === 'object') {
    try {
      // Try to serialize the object
      JSON.stringify(value);
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
};

/**
 * Recursively sanitizes an object by removing non-serializable properties
 * @param {any} obj - The object to sanitize
 * @param {number} maxDepth - Maximum depth to traverse (prevents infinite loops)
 * @param {number} currentDepth - Current depth in recursion
 * @returns {any} - The sanitized object
 */
export const sanitizeObject = (obj, maxDepth = 3, currentDepth = 0) => {
  if (currentDepth >= maxDepth) return '[Max Depth Reached]';

  if (obj === null || obj === undefined) return obj;
  if (
    typeof obj === 'string' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean'
  )
    return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item, maxDepth, currentDepth + 1));
  }

  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip properties that might contain circular references or non-serializable data
      if (
        [
          'parent',
          'original',
          'sql',
          'error@context',
          'stack',
          'cause',
        ].includes(key)
      ) {
        continue;
      }

      try {
        const sanitizedValue = sanitizeObject(
          value,
          maxDepth,
          currentDepth + 1,
        );
        if (isSerializable(sanitizedValue)) {
          sanitized[key] = sanitizedValue;
        }
      } catch (e) {
        // Skip this property if it causes issues
        continue;
      }
    }
    return sanitized;
  }

  return obj;
};

/**
 * Sanitizes error objects specifically for React components
 * @param {any} error - The error object to sanitize
 * @returns {Object} - A sanitized error object safe for React
 */
export const sanitizeErrorObject = (error) => {
  if (error === null || error === undefined) {
    return { message: 'An unknown error occurred' };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error instanceof Error) {
    return {
      message: error.message || 'An error occurred',
      name: error.name,
      stack: import.meta.env.MODE === 'development' ? error.stack : undefined,
    };
  }

  if (typeof error === 'object') {
    const sanitized = sanitizeObject(error);

    // Ensure we have a message property
    if (!sanitized.message && !sanitized.text) {
      sanitized.message = 'An error occurred';
    }

    // Normalize message property
    if (sanitized.text && !sanitized.message) {
      sanitized.message = sanitized.text;
      delete sanitized.text;
    }

    return sanitized;
  }

  return { message: 'An error occurred' };
};

/**
 * Sanitizes socket message objects to prevent React serialization issues
 * @param {Object} message - The socket message object
 * @returns {Object} - A sanitized message object safe for React
 */
export const sanitizeSocketMessage = (message) => {
  if (!message || typeof message !== 'object') {
    return { message: 'Invalid message received' };
  }

  const sanitized = sanitizeObject(message);

  // Ensure we have essential properties
  if (!sanitized.payload && !sanitized.text && !sanitized.message) {
    sanitized.message = 'Message received';
  }

  return sanitized;
};
